/**
 * SMSWiser prepared drops list queries
 * For Media Devoted | Created By Ciro Autiero
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query';

import type { PreparedDrop } from '@/features/smswiser-campaigns/types/workspace.types';
import api from '@/services/api';

import { keys } from '../keys';
import { unwrapApiData } from '../utils';

export async function fetchPreparedDropsByCountry(
  countryKey: string,
  workDate?: string,
): Promise<PreparedDrop[]> {
  const params: Record<string, string> = {};
  if (workDate) params.workDate = workDate;
  const res = await api.get(
    `/smswiser/countries/${encodeURIComponent(countryKey)}/prepared-drops`,
    { params },
  );
  return unwrapApiData<PreparedDrop[]>(res.data);
}

export function usePreparedDropsList(countryKey: string, wiserId: number, workDate?: string) {
  const params: Record<string, string> = {};
  if (workDate) params.workDate = workDate;

  return useQuery({
    queryKey: keys.preparedDrops(countryKey, wiserId, workDate),
    queryFn: async () => {
      const res = await api.get(
        `/smswiser/campaigns/${countryKey}/${wiserId}/prepared-drops`,
        { params },
      );
      return unwrapApiData<PreparedDrop[]>(res.data);
    },
    enabled: Boolean(countryKey) && wiserId > 0,
    staleTime: 10_000,
  });
}

/** List all prepared drops for a country (all campaigns). Includes campaignName per row. */
export function usePreparedDropsByCountry(countryKey: string, workDate?: string) {
  const params: Record<string, string> = {};
  if (workDate) params.workDate = workDate;

  return useQuery({
    queryKey: keys.preparedDropsByCountry(countryKey, workDate),
    queryFn: () => fetchPreparedDropsByCountry(countryKey, workDate),
    enabled: Boolean(countryKey),
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}
